import { ADMIN_USER_TYPES, type StaffListItem } from '@/definitions/staff'
import { getInitials } from '@/utils/nameUtils'

/**
 * The name to head a staff member's card with.
 *
 * Given and family name only — the honorifics and credentials the record also
 * carries are detail fields, and are listed as their own rows once the card is
 * open rather than crowding the name.
 */
export const staffDisplayName = (staff: StaffListItem): string =>
  [staff.name_given, staff.name_family].filter(Boolean).join(' ').trim()

/**
 * The initials to stand in for a staff member, who has no avatar in this app.
 *
 * Defers to the shared `getInitials`, which already handles the one-name and
 * no-name cases and falls back to the email — so the badge is never an empty
 * circle.
 */
export const staffInitials = (staff: StaffListItem): string =>
  getInitials(staffDisplayName(staff), staff.email)

/**
 * The role as CS reads it.
 *
 * `staff_admin` and `admin` are the same standing and both render as Admin. An
 * unrecognized type is shown as it is stored rather than guessed at or hidden,
 * so a role added to the backend shows up here as itself instead of vanishing.
 */
export const staffRoleLabel = (userType: string): string => {
  if (ADMIN_USER_TYPES.includes(userType)) {
    return 'Admin'
  }
  if (userType === 'staff') {
    return 'Staff'
  }
  return userType
}

/**
 * The role to badge a staff member with in the list, or null for plain staff.
 *
 * Plain staff are the default and the majority, so badging them says nothing;
 * every other role is worth seeing without opening the card. That deliberately
 * includes the granular role names — `MANAGER`, `PROVIDER` — which reached the
 * `user_type` column when it was backfilled from `staff_role`. Showing them as
 * they are stored is what makes an organization whose staff carry those values
 * legible, instead of presenting all of them as indistinguishable staff.
 */
export const staffRoleChipLabel = (userType: string): string | null =>
  userType === 'staff' ? null : staffRoleLabel(userType)
